import {
  Grid,
  Typography,
  withStyles,
  WithStyles,
  Theme,
  Hidden
} from '@material-ui/core'
import { Fragment } from 'react'
import { UpdateStatus } from '../../../actions/types'
import { DeleteShowingArgs } from '../../../services/showings'
import { BucketObject } from '../../../services/storage/types'
import i18n from '../../../translations/i18n'
import { Apartment } from '../../../types/apartments'
import { Showing } from '../../../types/showings'
import { IndexedApplications } from '../../../types/states'
import { User } from '../../../types/users'
import ShowingsList from '../../Showings/Overview/ShowingsList'
import { AddressAndMedia, ApartmentInfo } from './Containers'

const styles = ({ breakpoints }: Theme) => ({
  primaryBtn: {
    backgroundColor: '#4f9b44',
    padding: '8px',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  root: {
    backgroundColor: 'inherit',
    width: 'auto' as any
  },
  title: {
    textAlign: 'center' as any,
    margin: '0.5em 0.5em 0.1em 0.35em'
  },
  infoTitle: {
    paddingBottom: '20px',
    textAlign: 'left' as any
  },
  centerTitle: {
    textAlign: 'center' as any
  },
  leftTitle: {
    textAlign: 'left' as any,
    padding: '10px 5px 0px 0'
  },
  rightTitle: {
    textAlign: 'right' as any,
    padding: '10px 5px 0px 0'
  },
  suggestContainer: {
    float: 'right' as const,
    [breakpoints.down('md')]: {
      float: 'none' as const
    }
  },
  mediaContainer: {
    padding: '0 40px 20px 10px',
    [breakpoints.down('md')]: {
      padding: '0 40px 20px 30px'
    },
    [breakpoints.down('xs')]: {
      padding: '0 0 20px 0'
    }
  },
  textContainer: {
    padding: '0',
    textAlign: 'left' as any
  },
  informationContainer: {
    maxWidth: '100%',
    marginBottom: '40px'
  },
  informationRigthSideContainer: {
    flexBasis: 'initial',
    paddingTop: '30px'
  },
  calendar: {
    paddingTop: '10px'
  },
  reserved: {
    backgroundColor: '#f50057',
    borderRadius: '40px'
  },
  addressAndMedia: {
    padding: '8px',
    [breakpoints.down('sm')]: {
      padding: '0'
    }
  },
  addressAndMediaTitle: {
    padding: '8px 0px 8px'
  },
  line: {
    margin: '0',
    padding: '0',
    width: '100%',
    borderBottom: '1px solid #E5E5E5'
  },
  cancelApartment: {
    backgroundColor: '#fff',
    marginRight: '10px',
    color: '#ff0000',
    border: '1px solid #ff0000',
    padding: '9px',
    fontSize: ' 0.8125rem',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#fff',
      color: '#c30e0d',
      border: '1px solid #c30e0d',
      boxShadow: 'none'
    }
  },
  startMarketing: {
    backgroundColor: '#4f9b44',
    color: '#fff',
    border: '1px solid #4f9b44',
    padding: '9px',
    fontSize: ' 0.8125rem',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#3f7c36',
      //color: '#3f7c36',
      border: '1px solid #3f7c36',
      boxShadow: 'none'
    }
  },
  disabled: {
    border: 'none'
  },
  mt20: {
    marginTop: '20px'
  },
})

interface ApartmentInformationProps extends WithStyles {
  apartment: Apartment
  user: User | undefined
  futureShowings: Showing[]
  files: BucketObject[]
  t: i18n.TFunction
  isAdmin: boolean
  deleteShowing: (args: DeleteShowingArgs) => void
  updateStatus: UpdateStatus
  applications: IndexedApplications
}

const ApartmentInformation = ({
  t,
  classes,
  apartment,
  user,
  files,
  futureShowings,
  isAdmin,
  deleteShowing,
  updateStatus,
  applications
}: ApartmentInformationProps) => (
  <Grid
    container
    item
    className={classes.root}
    md={12}
    spacing={0}
    justify='space-between'
    style={{ overflow: "hidden" }}
  >
    {apartment?.information?.address && (
      <Fragment>
        <Grid item xs={12} sm={12} lg={7} className={classes.mediaContainer}>
          <Grid container direction='column'>
            <AddressAndMedia
              t={t}
              files={files}
              classes={classes}
              apartment={apartment}
            />
          </Grid>
          {futureShowings.length > 0 && isAdmin && (
            <Grid item xs={12} sm={8} className={classes.informationContainer}>
              <div className={classes.line} />
              <ShowingsList
                t={t}
                showings={futureShowings}
                useCase='confirmed'
                deleteShowing={deleteShowing}
              />
              <div className={classes.line} />
              <ShowingsList
                t={t}
                showings={futureShowings}
                useCase='waiting'
                deleteShowing={deleteShowing}
              />
            </Grid>
          )}
          <Hidden mdDown>
            <Grid item xs={12} className={classes.informationContainer}>
              <Typography className={classes.infoTitle} variant='h5'>
                {t('apartment.list.information.notes')}
              </Typography>
              <Typography variant='body1'>
                <pre className='MuiTypography-root MuiTypography-body1'>
                  {apartment?.information?.notes || ''}
                </pre>
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.informationContainer}>
              <Typography className={classes.infoTitle} variant='h5'>
                {t('apartment.list.information.damages')}
              </Typography>
              <Typography variant='body1'>
                <pre className='MuiTypography-root MuiTypography-body1'>
                  {apartment?.information?.damages || ''}
                </pre>
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.informationContainer}>
              <Typography className={classes.infoTitle} variant='h5'>
                {t('apartment.list.information.renovations')}
              </Typography>
              <Typography variant='body1'>
                <pre className='MuiTypography-root MuiTypography-body1'>
                  {apartment?.information?.renovations || ''}
                </pre>
              </Typography>
            </Grid>
          </Hidden>
          {/* <Grid item xs={12} className={classes.informationContainer}>
            <Typography className={classes.infoTitle} variant='h5'>
              {t('apartment.list.information.energyCertification')}
            </Typography>
            <Typography variant='body1'>
              {t(
                apartment?.information?.energyCertification === 'default'
                  ? 'apartment.list.information.energyCertNotRequired'
                  : apartment?.information?.energyCertification || ''
              )}
            </Typography>
          </Grid> */}
        </Grid>
        {/* <Grid
          container
          direction='column'
          item
          sm={12}
          md={12}
          lg={5}
          className={classes.textContainer}
          justify='space-between'
        > */}
        <ApartmentInfo
          t={t}
          classes={classes}
          apartment={apartment}
          user={user}
          updateStatus={updateStatus}
          isAdmin={isAdmin}
          applications={applications}
        />
        {/* </Grid> */}
      </Fragment>
    )}
  </Grid>
)

export default withStyles(styles)(ApartmentInformation)
